import { useSubscription } from '@/composables/subscription'
import routesLimited from '@/constants/routesLimited'
import { isCypress } from '@/helpers/isCypress'
import { buildingService } from '@/services/api'
import { useBuildingStore } from '@/stores/modules/building'
import { useSettingStore } from '@/stores/modules/setting'
import { useLiaStore } from '@/stores/system/lia'
import { useNavStore } from '@/stores/system/nav'
import { useTourStore } from '@/stores/system/tour'
import { useUserStore } from '@/stores/system/user'
import { storeToRefs } from 'pinia'
import { driver, type Driver, type DriveStep } from 'driver.js'
import { router } from '@/plugins/router'
import { useWindowSize } from '@vueuse/core'
import { effectScope, watch, type Ref } from 'vue'
import { useDisplay } from 'vuetify'
import {
	bandFor,
	gateOutcome,
	isIncomplete,
	resolveSteps,
	type BandAware,
	type BandSpec,
	type GateResult,
	type TourLogEntry,
	type TourSkip,
	type TourSkipReason,
} from '@/helpers/tourBand'

export type GateContext = {
	checkIfSubscribedTo: ReturnType<typeof useSubscription>['checkIfSubscribedTo']
	settings: ReturnType<typeof useSettingStore>['getData'] | undefined
}

/**
 * Selektor zur Tour-Kennung. Die Tour findet Elemente NUR über `data-tour`
 * (nie über Klassen oder Verschachtelung), damit ein Umbau der Oberfläche
 * keinen Schritt still verliert. Ein Element darf mehrere Kennungen tragen
 * (durch Leerzeichen getrennt), mehrere Elemente dieselbe (Zustände eines
 * Knopfs, Listenzeilen) — dann gilt das erste sichtbare.
 */
export const tourAnchor = (id: string) => `[data-tour~="${id}"]`

/** Markiert eigene Oberfläche der Tour (Popover, Overlay, Ladehinweis). */
export const TOUR_UI_ATTR = 'data-tour-ui'

/** Fenster gerendert, aber noch unsichtbar, bis der Highlight-Kasten angekommen ist (App.vue). */
const POPOVER_PENDING_CLASS = 'aaremo-tour-popover--pending'

type TourStepBase = {
	id: string
	title?: string
	text: string
	/** Kennung des Ziel-Elements (`data-tour`, siehe tourAnchor). Fehlt = Fenster mittig. */
	anchor?: string
	placement?: 'top' | 'bottom' | 'left' | 'right' | 'over'
	/**
	 * Optional gating predicate. `false` = not permitted, `{ skip: 'data' }` =
	 * nothing to show (e.g. no meters). May be async to probe the API. A thrown
	 * error is a technical failure: the step is skipped and the run counts as
	 * incomplete — never as "not permitted".
	 */
	gate?: (ctx: GateContext) => GateResult | Promise<GateResult>
	/** Route name to push before this step is shown. */
	route?: string
	/**
	 * Extra setup before show (open drawer, dialog, level). Receives the run's
	 * abort signal: after every await it must check `signal.aborted` and stop —
	 * no UI action may happen once the tour has ended.
	 */
	prepare?: (signal: AbortSignal) => Promise<void> | void
	/** Anchor lives in Drawer's #append slot; expand the rail first. */
	needsExpandedDrawer?: boolean
	/**
	 * Show-time guard, evaluated AFTER prepare/navigation. `false` (or
	 * `{ skip }`) skips the step instead of rendering it on the wrong page —
	 * e.g. the building steps when navigation to `building/:id` never happened.
	 */
	verifyOnShow?: () => boolean | TourSkip
}

/** A tour step; `bands`/`variants` adapt it per layout band (AAR-1993). */
export type TourStepDefinition = BandAware<TourStepBase>

/** Why a run ended. Only completed/skipped are remembered as "seen". */
export type TourEndReason =
	| 'completed' // Fertig
	| 'skipped' // ✕, „Tour überspringen“, Esc
	| 'band' // layout band changed mid-run
	| 'external' // navigation, unmount, another tour
	| 'logout'
	| 'error' // technical failure of the run itself
	| 'empty' // no step was showable

/** Layout band a run started in, and how to read the band now. */
export type TourBandContext = { current: string; now: () => string }

/** Same variant for the narrow and the medium band. */
const belowWide = (
	patch: Partial<TourStepBase>
): Partial<Record<'narrow' | 'medium', Partial<TourStepBase>>> => ({
	narrow: patch,
	medium: patch,
})

/**
 * True when the SPA is actually on a building detail route (`building/:id`).
 * The building tour steps use this as a `verifyOnShow` guard so they are skipped
 * — rather than rendered as a centered popover on the wrong page — when the
 * pre-step navigation to the building never happened (no building).
 */
function isOnBuildingRoute(): boolean | TourSkip {
	const route = router.currentRoute.value
	return (route.name === 'building' && !!route.params?.id) || { skip: 'data' }
}

/**
 * Steht der Menüpunkt im Menü dieses Benutzers? Dieselbe Quelle wie Drawer.vue
 * (Nav-Store, nach Rolle/Abo/Berechtigung gefiltert) — sonst übersprungen mit
 * Grund `permission` statt als fehlender Anker.
 */
function inNav(...names: string[]): GateResult {
	type NavRoute = {
		name?: unknown
		meta?: { name?: unknown }
		children?: NavRoute[]
	}
	// Wie Drawer.vue (getTourProps): Name der Route, sonst meta.name.
	const walk = (routes: NavRoute[] | undefined): boolean =>
		!!routes?.some(
			(route) =>
				[route.name, route.meta?.name].some((n) => names.includes(String(n))) ||
				walk(route.children)
		)
	return walk(useNavStore().nav as NavRoute[]) || { skip: 'permission' }
}

/** Buildings of the user; API errors propagate (technical failure, not "none"). */
async function loadBuildings(): Promise<{ id: number }[]> {
	const store = useBuildingStore()
	const known =
		(store.data as { id: number }[] | undefined) ??
		(store.dataInit as { id: number }[] | undefined)
	if (known && known.length > 0) return known
	const loaded = await store.load()
	if (Array.isArray(loaded)) return loaded as { id: number }[]
	return (store.dataInit as { id: number }[] | undefined) ?? []
}

/**
 * Tour step registry. Steps whose gate fails are skipped with a reason; steps
 * whose anchor never appears are skipped and logged (see runStepPrepare).
 */
function buildStepRegistry(): TourStepDefinition[] {
	return [
		{
			id: 'welcome',
			title: 'Willkommen im Portal',
			text:
				'Wir zeigen Ihnen in wenigen Schritten die wichtigsten Funktionen.' +
				' Sie können die Tour jederzeit über das ✕ oben rechts beenden.',
		},
		{
			id: 'toolbar',
			title: 'Navigation',
			text:
				'Über diesen Bereich können Sie die seitliche Navigation ein- und ausklappen.',
			anchor: 'navRailToggle',
			placement: 'bottom',
			variants: {
				// Schmal ist die Navigation zu und öffnet über das Menü-Symbol (⋮).
				narrow: {
					text: 'Über das Menü-Symbol öffnen Sie die Navigation.',
					anchor: 'navToggle',
				},
			},
		},
		{
			id: 'reportButton',
			title: 'Anliegen melden',
			text:
				'Sie haben eine Frage oder möchten einen Schaden melden? Hier senden Sie Ihr Anliegen direkt an Ihre Verwaltung.',
			anchor: 'reportButton',
			placement: 'bottom',
			gate: ({ checkIfSubscribedTo, settings }) =>
				!settings?.hide_report &&
				checkIfSubscribedTo(routesLimited.subModules.reports).allowed,
		},
		{
			id: 'notifyButton',
			title: 'Benachrichtigungen',
			text:
				'Hier finden Sie Mitteilungen Ihrer Verwaltung, neue Dokumente und Aktualisierungen.',
			anchor: 'notifyButton',
			placement: 'bottom',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.notifications).allowed,
		},
		{
			id: 'notes',
			title: 'Aktuelles aus Ihrer Liegenschaft',
			text:
				'Allgemeine Informationen und liegenschaftsbezogene Neuigkeiten erscheinen hier.',
			// Seit Dashboard v3 ein Widget im Raster (eingeklappte Bereiche
			// rendern es nicht).
			anchor: 'widget-notes',
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.notes).allowed,
			// Unter 960 px ist das Dashboard einspaltig: rechts ist kein Platz,
			// die Karte selbst kann höher als der Bildschirm sein.
			variants: belowWide({ anchor: 'widget-notes-head', placement: 'bottom' }),
		},
		{
			// Schmal ersetzt dieser Schritt die vier Menü-Schritte: die Navigation
			// ist dort ein überlagerndes Menü, das die Tour über den Toolbar-Knopf
			// öffnet und im nächsten Schritt bzw. am Ende wieder schließt.
			id: 'navMenu',
			bands: ['narrow'],
			title: 'Menü',
			text:
				'Im Menü erreichen Sie alle Bereiche des Portals, je nach Freigabe Ihrer Verwaltung zum Beispiel Dokumente, Belegprüfung sowie Hilfe und FAQ.',
			anchor: 'navMenu',
			placement: 'bottom',
			needsExpandedDrawer: true,
			gate: ({ checkIfSubscribedTo }) =>
				[
					routesLimited.subModules.documents,
					routesLimited.subModules.review_documents,
					routesLimited.subModules.reports,
					routesLimited.subModules.faqs,
				].some((sub) => checkIfSubscribedTo(sub).allowed),
		},
		{
			id: 'navDocuments',
			bands: ['medium', 'wide'],
			variants: { medium: { needsExpandedDrawer: true } },
			title: 'Dokumente',
			text:
				'Unter Dokumente sehen Sie alle für Sie freigegebenen Unterlagen Ihrer Verwaltung.',
			// Kennungen der Menüpunkte setzt Drawer.vue (getTourProps) aus dem Routennamen.
			anchor: 'navDocuments_group',
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.documents).allowed &&
				inNav('documents', 'documenttypes', 'documents_group'),
		},
		{
			id: 'navReviewDocuments',
			bands: ['medium', 'wide'],
			variants: { medium: { needsExpandedDrawer: true } },
			title: 'Belegprüfung',
			text:
				'Hier können Sie die zur Belegprüfung freigegebenen Unterlagen einsehen und – sofern Sie dazu berechtigt sind – im Rahmen der Belegprüfung freigeben.',
			anchor: 'navReviewDocuments',
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.review_documents).allowed &&
				inNav('reviewDocuments'),
		},
		{
			id: 'navPendingRequests',
			bands: ['medium', 'wide'],
			variants: { medium: { needsExpandedDrawer: true } },
			title: 'Offene Anfragen',
			text:
				'Stammdatenänderungen und Zählerstands-Freigaben Ihrer Verwaltung finden Sie hier.',
			anchor: 'navPendingRequests',
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.reports).allowed &&
				// Nur Admin/Team (Route limited): für normale Benutzer `permission`.
				inNav('pendingRequests'),
		},
		{
			id: 'navFaqs',
			bands: ['medium', 'wide'],
			variants: { medium: { needsExpandedDrawer: true } },
			title: 'Hilfe & FAQ',
			text:
				'Im FAQ-Bereich finden Sie Antworten auf häufige Fragen rund um das Portal und die Zusammenarbeit mit Ihrer Verwaltung.',
			anchor: 'navFaqs',
			placement: 'right',
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.faqs).allowed &&
				// Zusätzlich Rollenrecht faq_enabled (Route limited).
				inNav('faqs'),
		},
		{
			id: 'themeLocaleProfile',
			title: 'Sprache, Design & Profil-Menü',
			text:
				'Hinter Ihrem Profil-Symbol oben rechts können Sie zusätzlich die Sprache umstellen, das Design (hell/dunkel) wechseln und sich abmelden.',
			anchor: 'profileButton',
			placement: 'bottom',
		},
		{
			id: 'buildingDetail',
			title: 'Ihre Liegenschaft im Detail',
			text:
				'Hier sehen Sie alle Informationen rund um Ihre Liegenschaft und die zugehörigen Einheiten – je nach Freigabe Ihrer Verwaltung z. B. Dokumente, Notizen, Personen, Zähler, Buchhaltung oder Belege.',
			// Die Registerkarten der Liegenschaft (BuildingNav.vue).
			anchor: 'buildingTabs',
			placement: 'bottom',
			variants: {
				// Schmal liegen die Registerkarten als feste Leiste am unteren Rand
				// (Building.vue) und zeigen nur die aktive, der Rest steckt im Menü.
				narrow: {
					placement: 'top',
					text:
						'Hier sehen Sie alle Informationen rund um Ihre Liegenschaft und die zugehörigen Einheiten. Über die Leiste unten und ihr Menü-Symbol wechseln Sie zu weiteren Bereichen, je nach Freigabe Ihrer Verwaltung z. B. Dokumente, Notizen, Personen, Zähler, Buchhaltung oder Belege.',
				},
			},
			verifyOnShow: isOnBuildingRoute,
			gate: ({ checkIfSubscribedTo }) =>
				checkIfSubscribedTo(routesLimited.subModules.buildings).allowed,
			prepare: async (signal) => {
				const buildingId = (await loadBuildings())[0]?.id
				if (signal.aborted || !buildingId) return
				const current = router.currentRoute.value
				if (
					current.name !== 'building' ||
					Number(current.params?.id) !== buildingId
				) {
					await router
						.push({ name: 'building', params: { id: buildingId } })
						.catch(() => undefined)
				}
			},
		},
		{
			id: 'buildingMeters',
			title: 'Zählerstände erfassen',
			text:
				'Auf der Registerkarte „Zähler" können Sie regelmäßig Ihre Zählerstände – Wasser, Strom, Wärme – erfassen und an die Verwaltung übermitteln.',
			// Passen nicht alle Registerkarten in die Breite, landet „Zähler" im
			// Menü am Ende der Leiste; dann trägt dessen Knopf die Kennung (Tabs.vue).
			anchor: 'buildingMeters',
			placement: 'bottom',
			variants: {
				medium: {
					text:
						'Auf der Registerkarte „Zähler" erfassen Sie regelmäßig Ihre Zählerstände für Wasser, Strom und Wärme und übermitteln sie an die Verwaltung. Ist zu wenig Platz, finden Sie die Registerkarte im Menü am Ende der Leiste.',
				},
				// Schmal ist nur die aktive Registerkarte sichtbar, alle anderen
				// liegen im Menü der festen Leiste unten.
				narrow: {
					placement: 'top',
					text:
						'Über das Menü-Symbol der Leiste unten öffnen Sie die Registerkarte „Zähler". Dort erfassen Sie regelmäßig Ihre Zählerstände für Wasser, Strom und Wärme und übermitteln sie an die Verwaltung.',
				},
			},
			verifyOnShow: isOnBuildingRoute,
			gate: async ({ checkIfSubscribedTo, settings }) => {
				if (settings?.hide_meter_reading) return false
				if (!checkIfSubscribedTo(routesLimited.subModules.meters).allowed) {
					return false
				}
				const buildingId = (await loadBuildings())[0]?.id
				if (!buildingId) return { skip: 'data' }
				const { data } = await buildingService.itemModuleGet<
					Api.Collection<{ id: number }>
				>(buildingId, 'meters')
				return (Array.isArray(data?.data) && data.data.length > 0) || {
					skip: 'data',
				}
			},
			prepare: async () => {
				// buildingDetail already navigated to /buildings/:id — stay there.
				// Just give BuildingNav.fillTabs() a tick to populate the tabs.
				await new Promise((r) => setTimeout(r, 100))
			},
		},
		{
			id: 'profileIntro',
			title: 'Ihr Profil',
			text:
				'In Ihrem Profil sehen Sie, welche Daten Ihre Verwaltung über Sie gespeichert hat: persönliche Stammdaten, Liegenschaften und Ihre Meldungen.',
			anchor: 'profileCard',
			placement: 'bottom',
			route: 'profile',
		},
		{
			id: 'profileReportsTab',
			title: 'Ihre Meldungen im Überblick',
			text:
				'Auf der Registerkarte „Meldungen" finden Sie alle Anliegen, die Sie bereits an Ihre Verwaltung gesendet haben – inklusive aktuellem Bearbeitungsstatus.',
			anchor: 'profileReports',
			placement: 'top',
			route: 'profile',
			gate: ({ checkIfSubscribedTo, settings }) =>
				!settings?.hide_report &&
				checkIfSubscribedTo(routesLimited.subModules.reports).allowed,
			prepare: async (signal) => {
				const tab = firstVisible('profileReportsTab')
				if (signal.aborted) return
				// Nur den Reiter selbst anklicken — liegt er im Überlauf-Menü, trägt
				// dessen Knopf die Kennung, und der öffnete nur das Menü.
				if (
					tab?.getAttribute('role') === 'tab' &&
					tab.getAttribute('aria-selected') !== 'true'
				) {
					tab.click()
					await new Promise((r) => setTimeout(r, 200))
				}
			},
		},
		{
			// Schmal blendet ButtonWrapper alle Profil-Knöpfe aus und zeigt nur
			// „Aktionen“. Dieser Schritt ersetzt dort Passwort, Einstellungen und
			// Stammdaten. Kein Gate: „Passwort ändern“ hat keins, das Menü ist
			// also immer da (die Gates der drei Schritte zusammengefasst).
			id: 'profileActions',
			bands: ['narrow'],
			title: 'Aktionen',
			text:
				'Über diesen Knopf öffnen Sie die Aktionen zu Ihrem Profil: Passwort ändern, in den Einstellungen festlegen, worüber Sie per E-Mail informiert werden, und Änderungen Ihrer persönlichen Daten an die Verwaltung senden. Was davon verfügbar ist, hängt von der Freigabe Ihrer Verwaltung ab.',
			anchor: 'profileActions',
			placement: 'top',
			route: 'profile',
		},
		{
			id: 'profileChangePassword',
			bands: ['medium', 'wide'],
			title: 'Passwort ändern',
			text:
				'Hier können Sie jederzeit Ihr Passwort ändern. Wir empfehlen, das initiale Passwort beim ersten Login zu ersetzen.',
			anchor: 'profileChangePassword',
			placement: 'top',
			route: 'profile',
		},
		{
			id: 'profileSettings',
			bands: ['medium', 'wide'],
			title: 'Benachrichtigungs-Einstellungen',
			text:
				'Über die Einstellungen entscheiden Sie selbst, zu welchen Ereignissen Sie per E-Mail informiert werden möchten – z. B. neue Dokumente, Notizen oder Meldungs-Updates.',
			anchor: 'profileSettings',
			placement: 'top',
			route: 'profile',
			gate: ({ checkIfSubscribedTo }) =>
				[
					routesLimited.subModules.notifications,
					routesLimited.subModules.notes,
				].every((sub) => checkIfSubscribedTo(sub).allowed),
		},
		{
			id: 'profilePersonUpdate',
			bands: ['medium', 'wide'],
			title: 'Stammdaten aktualisieren',
			text:
				'Über diesen Knopf können Sie eine Änderung Ihrer persönlichen Daten an die Verwaltung senden. Die Verwaltung prüft und übernimmt die Änderung.',
			anchor: 'profilePersonUpdate',
			placement: 'top',
			route: 'profile',
			gate: ({ checkIfSubscribedTo, settings }) =>
				!settings?.hide_update_requests &&
				checkIfSubscribedTo(
					routesLimited.subMethods.notification_person_update
				).allowed,
		},
		{
			id: 'propertyManagers',
			title: 'Ihre Verwaltung',
			text:
				'Hier sehen Sie Ihre Hausverwaltung mit Ansprechpartner und Adresse — immer griffbereit, wenn Sie Kontakt aufnehmen möchten.',
			anchor: 'propertyManagers',
			placement: 'top',
			route: 'profile',
		},
		{
			id: 'personDetail',
			title: 'Ihre persönlichen Daten',
			text:
				'Auf Ihrer Personenseite finden Sie alle direkt zugeordneten Informationen: persönliche Dokumente, E-Mail-Adressen, Telefonnummern sowie Ihre Rollen und Berechtigungen.',
			anchor: 'personPage',
			placement: 'bottom',
			// Schmal ist der Seiten-Container höher als der Bildschirm: das
			// Fenster fände keinen Platz. Die Info-Karte oben reicht als Anker.
			variants: { narrow: { anchor: 'personCard' } },
			gate: ({ checkIfSubscribedTo }) => {
				if (!checkIfSubscribedTo(routesLimited.subModules.persons).allowed) {
					return false
				}
				return (useUserStore().whoami?.persons?.length ?? 0) > 0 || {
					skip: 'data',
				}
			},
			prepare: async (signal) => {
				const personId = useUserStore().whoami?.persons?.[0]?.id
				const current = router.currentRoute.value
				if (signal.aborted || !personId) return
				if (current.name !== 'person' || Number(current.params?.id) !== personId) {
					await router
						.push({ name: 'person', params: { id: personId } })
						.catch(() => undefined)
				}
			},
		},
		{
			id: 'privacyTos',
			title: 'Datenschutz & Nutzungsbedingungen',
			text:
				'Die aktuellen Datenschutz- und Nutzungsbedingungen können Sie hier jederzeit einsehen.',
			anchor: 'navPrivacyTos',
			placement: 'top',
			route: 'dashboard',
			// Schmal öffnet ensureDrawerExpanded nach dem Seitenwechsel das
			// überlagernde Menü (der Seitenwechsel selbst schließt es).
			needsExpandedDrawer: true,
		},
		{
			id: 'outro',
			title: 'Sie sind startklar',
			text:
				'Sie können diese Tour jederzeit über <strong>Profil → Einstellungen → „Einführungstour erneut starten"</strong> erneut starten.',
			variants: {
				narrow: {
					text:
						'Sie können diese Tour jederzeit über <strong>Profil → Aktionen → Einstellungen → „Einführungstour erneut starten"</strong> erneut starten.',
				},
			},
		},
	]
}

// ---------------------------------------------------------------------------
// Fensterbreite und Layout-Band
// ---------------------------------------------------------------------------
let sharedWidth: Ref<number> | undefined

/**
 * The raw window width, shared by every tour. Only the width is shared: each
 * tour classifies it with its own band edges (a shared composable would keep
 * the first caller's arguments).
 */
export function tourWindowWidth(): Ref<number> {
	sharedWidth ??= effectScope(true).run(() => useWindowSize().width)!
	return sharedWidth
}

/** Band edges of the portal and dashboard tours: Vuetify's sm and md. */
export function useTourBandSpec(): () => BandSpec<'narrow' | 'medium' | 'wide'> {
	const { thresholds } = useDisplay()
	return () => [
		{ band: 'narrow', min: 0 },
		{ band: 'medium', min: thresholds.value.sm },
		{ band: 'wide', min: thresholds.value.md },
	]
}

// ---------------------------------------------------------------------------
// Anker finden: versteckt? erreichbar?
// ---------------------------------------------------------------------------

/**
 * Stage a — hidden? Position-independent: an element below the visible area
 * is NOT hidden (we scroll to it later). Hidden means detached, 0×0,
 * `visibility: hidden`, inside `inert` (e.g. a closed overlay drawer) or
 * horizontally outside the document (off-canvas).
 */
function isRenderable(el: HTMLElement): boolean {
	if (!el.isConnected) return false
	const box = el.getBoundingClientRect()
	if (box.width <= 0 || box.height <= 0) return false
	if (el.closest('[inert]')) return false
	if (getComputedStyle(el).visibility === 'hidden') return false
	const docWidth = document.documentElement.clientWidth
	return box.right > 0 && box.left < docWidth
}

/** Erstes sichtbares Element mit dieser Tour-Kennung. */
function firstVisible(anchor: string): HTMLElement | null {
	for (const el of document.querySelectorAll<HTMLElement>(tourAnchor(anchor))) {
		if (isRenderable(el)) return el
	}
	return null
}

/** Wartet, bis ein Element mit dieser Tour-Kennung sichtbar ist. */
export async function findAnchor(
	anchor: string,
	timeoutMs = 2500,
	signal?: AbortSignal
): Promise<HTMLElement | null> {
	const deadline = Date.now() + timeoutMs
	while (Date.now() < deadline) {
		if (signal?.aborted) return null
		const hit = firstVisible(anchor)
		if (hit) return hit
		await new Promise((r) => setTimeout(r, 80))
	}
	return null
}

const nextFrame = () => new Promise((r) => requestAnimationFrame(r))

/**
 * Scroll the anchor into view ourselves and wait until its position holds.
 * `center` scrolls even if the anchor counts as in view — for anchors under a
 * fixed bar, which this box check cannot see.
 */
async function bringIntoView(
	el: HTMLElement,
	signal: AbortSignal,
	center = false
) {
	const box = el.getBoundingClientRect()
	const viewHeight = window.innerHeight
	const inView =
		box.top >= 0 &&
		box.bottom <= viewHeight &&
		box.left >= 0 &&
		box.right <= document.documentElement.clientWidth
	if (center || !inView) {
		el.scrollIntoView({
			block: !center && box.height > viewHeight ? 'start' : 'center',
			inline: 'nearest',
		})
	}
	let last = el.getBoundingClientRect().top
	let stable = 0
	const deadline = Date.now() + 600
	while (stable < 2 && Date.now() < deadline) {
		await nextFrame()
		if (signal.aborted) return
		const top = el.getBoundingClientRect().top
		stable = Math.abs(top - last) < 1 ? stable + 1 : 0
		last = top
	}
}

/**
 * Stage b — covered? Hit-test points inside the anchor's visible part. While a
 * tour runs, driver.js sets `.driver-active * { pointer-events: none }`, so
 * hit-testing never reaches page content; the class is lifted for the
 * synchronous probe only (no paint in between). Hits on the anchor itself, a
 * child or an ancestor count as visible; the tour's own UI (`data-tour-ui`:
 * popover, overlay, waiting hint) is ignored. Covered = fewer than 2 of 5
 * points reach the anchor (a fixed bar over its centre).
 */
const COVER_SAMPLES = [
	[0.5, 0.5],
	[0.2, 0.2],
	[0.8, 0.2],
	[0.2, 0.8],
	[0.8, 0.8],
] as const

function isCovered(el: HTMLElement): boolean {
	const box = el.getBoundingClientRect()
	const left = Math.max(box.left, 0)
	const right = Math.min(box.right, document.documentElement.clientWidth)
	const top = Math.max(box.top, 0)
	const bottom = Math.min(box.bottom, window.innerHeight)
	if (right - left < 1 || bottom - top < 1) return true
	const points = COVER_SAMPLES.map(
		([fx, fy]) => [left + (right - left) * fx, top + (bottom - top) * fy] as const
	)

	// Das Overlay erzeugt driver.js selbst, ohne Zugriff darauf, und erst am
	// Ende der Einblendung — darum direkt vor der Prüfung markieren (die
	// einzige Stelle, die es über seine Klasse findet).
	document
		.querySelectorAll('.driver-overlay')
		.forEach((overlay) => overlay.setAttribute(TOUR_UI_ATTR, ''))

	const body = document.body
	const blocked = body.classList.contains('driver-active')
	if (blocked) body.classList.remove('driver-active')
	let reached = 0
	try {
		for (const [x, y] of points) {
			const hit = document
				.elementsFromPoint(x, y)
				// Eigene Oberfläche der Tour verdeckt nichts.
				.find((e) => !e.closest(`[${TOUR_UI_ATTR}]`))
			if (hit && (hit === el || el.contains(hit) || hit.contains(el))) reached++
		}
	} finally {
		if (blocked) body.classList.add('driver-active')
	}
	return reached < 2
}

/**
 * driver.js clamps against `window.innerWidth`, which includes the page
 * scrollbar — near the right edge the popover slid under it. Pull it back
 * into the visible area.
 */
function keepInViewport(wrapper: HTMLElement) {
	const margin = 8
	const box = wrapper.getBoundingClientRect()
	const maxRight = document.documentElement.clientWidth - margin
	if (box.right > maxRight) {
		wrapper.style.left = `${Math.max(margin, maxRight - box.width)}px`
		wrapper.style.right = 'auto'
	}
}

/**
 * driver.js centres the anchor with `scrollIntoView({ inline: 'center' })`.
 * An anchor wider than its column then scrolls an `overflow: hidden` card
 * sideways — the column content stays shifted even after the tour. Undo that.
 */
function resetClippedScroll(anchor: Element) {
	for (let el = anchor.parentElement; el; el = el.parentElement) {
		if (el.scrollLeft !== 0 && getComputedStyle(el).overflowX === 'hidden') {
			el.scrollLeft = 0
		}
	}
}

/**
 * Wartet, bis ein Element zwei Frames lang stillsteht (Smooth-Scroll,
 * Dialog- und Slide-over-Übergänge), höchstens `maxMs`.
 */
function untilSettled(el: Element, maxMs = 800): Promise<void> {
	const deadline = Date.now() + maxMs
	return new Promise((resolve) => {
		let last = ''
		let still = 0
		const tick = () => {
			const r = el.getBoundingClientRect()
			const pos = `${r.x},${r.y},${r.width},${r.height}`
			still = pos === last ? still + 1 : 0
			last = pos
			if (still >= 2 || Date.now() > deadline) resolve()
			else requestAnimationFrame(tick)
		}
		requestAnimationFrame(tick)
	})
}

export async function waitForGone(selector: string, timeoutMs = 1500): Promise<void> {
	const deadline = Date.now() + timeoutMs
	while (Date.now() < deadline) {
		if (!document.querySelector(selector)) return
		await new Promise((r) => setTimeout(r, 50))
	}
}

// ---------------------------------------------------------------------------
// Laufprotokoll
// ---------------------------------------------------------------------------
const UNEXPECTED: readonly TourSkipReason[] = ['error', 'anchor', 'covered']

function logSkip(
	log: TourLogEntry[],
	tour: string,
	id: string,
	reason: TourSkipReason,
	detail?: string
) {
	log.push({ id, status: 'skipped', reason, detail })
	if (import.meta.env.DEV && UNEXPECTED.includes(reason)) {
		console.warn(`[tour] ${tour}: Schritt „${id}“ entfällt (${reason})`, detail ?? '')
	}
}

/** Runs of this page load, for Cypress and the dev console. */
function publishRun(
	tour: string,
	reason: TourEndReason,
	log: TourLogEntry[],
	band?: string
) {
	if (!import.meta.env.DEV && !isCypress()) return
	const w = window as unknown as {
		__aaremoTourRuns?: {
			tour: string
			band?: string
			reason: TourEndReason
			log: TourLogEntry[]
		}[]
	}
	;(w.__aaremoTourRuns ??= []).push({ tour, band, reason, log: [...log] })
}

// ---------------------------------------------------------------------------
// Auto-Start-Sperren
// ---------------------------------------------------------------------------
/** Tours ended by band change, navigation or error in this page load. */
const abortedThisLoad = new Set<string>()
const INCOMPLETE_PREFIX = 'aaremo-tour-incomplete:'

function markIncomplete(key: string) {
	abortedThisLoad.add(key)
	try {
		window.sessionStorage.setItem(INCOMPLETE_PREFIX + key, '1')
	} catch {
		/* private mode: the page-load lock above still holds */
	}
}

/**
 * No auto-start after an abort in the same page load (prevents a restart
 * loop when the width oscillates) and none after an incomplete run in the
 * same session (a failing API must not re-open the tour on every visit).
 */
export function autoStartBlocked(seenKey: number | string | undefined): boolean {
	if (seenKey === undefined || seenKey === null) return true
	const key = String(seenKey)
	if (abortedThisLoad.has(key)) return true
	try {
		return window.sessionStorage.getItem(INCOMPLETE_PREFIX + key) === '1'
	} catch {
		return false
	}
}

function persistOutcome(
	reason: TourEndReason,
	seenKey: number | string | undefined,
	log: TourLogEntry[]
) {
	if (seenKey === undefined || seenKey === null) return
	const tourStore = useTourStore()
	const key = String(seenKey)
	if (reason === 'completed') {
		if (isIncomplete(log)) markIncomplete(key)
		else tourStore.markCompleted(seenKey)
		return
	}
	if (reason === 'skipped') {
		tourStore.markSkipped(seenKey)
		return
	}
	if (reason === 'error') markIncomplete(key)
	else abortedThisLoad.add(key)
}

// ---------------------------------------------------------------------------
// Navigation (Drawer) öffnen und wieder schließen
// ---------------------------------------------------------------------------
const NAV_DRAWER = tourAnchor('navDrawer')
/** Menü-Knopf der Toolbar unter 600 px (⋮); öffnet den überlagernden Drawer. */
const NAV_DRAWER_TOGGLE = tourAnchor('navToggle')

/**
 * What the tour itself opened in the navigation, so it closes exactly that and
 * nothing the user had open: the overlay drawer below 600 px (`opened`) or the
 * rail expanded by hover (`expanded`).
 */
const tourDrawer = { opened: false, expanded: false }

const pause = (ms: number) => new Promise((r) => setTimeout(r, ms))
const navDrawer = () => document.querySelector<HTMLElement>(NAV_DRAWER)
const hasClass = (el: HTMLElement, name: string) =>
	el.classList.contains(`v-navigation-drawer--${name}`)

async function ensureDrawerExpanded() {
	const drawerEl = navDrawer()
	if (!drawerEl) return

	// Unter 600 px: temporärer Drawer, zu. Sein Zustand ist ein lokales ref in
	// Drawer.vue — öffnen wie der Nutzer, über den Menü-Knopf der Toolbar.
	if (hasClass(drawerEl, 'temporary')) {
		if (hasClass(drawerEl, 'active')) return
		// Erst den Routen-Beobachter von Vuetify laufen lassen: Er schließt den
		// temporären Drawer nach jedem Seitenwechsel (privacyTos wechselt vorher).
		await pause(50)
		// Tour inzwischen beendet (kein Abbruchsignal hier): nichts mehr öffnen.
		if (!currentRun || hasClass(drawerEl, 'active')) return
		const toggle = document.querySelector<HTMLElement>(NAV_DRAWER_TOGGLE)
		if (!toggle) return
		tourDrawer.opened = true
		toggle.click()
		await pause(300) // Einfahren des Drawers
		return
	}

	// We used to flip the `mini` cookie here, but @vueuse/useCookies
	// has per-instance change listeners — writes from useTour don't
	// reactively re-trigger Drawer.vue's `computed(() => cookies.get('mini'))`.
	// Instead, simulate a mouseenter on the v-navigation-drawer: Drawer.vue
	// sets `hover = true`, and Vuetify's `expand-on-hover` (configured as
	// `hover && drawer && mini`) expands the rail without any cookie write.
	if (!hasClass(drawerEl, 'rail')) return
	// Schon aufgeklappt (vorheriger Menü-Schritt): nicht erneut warten.
	if (hasClass(drawerEl, 'expand-on-hover') && hasClass(drawerEl, 'is-hovering')) {
		return
	}
	tourDrawer.expanded = true
	drawerEl.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }))
	// Wait for v-navigation-drawer rail->expanded transition.
	await pause(350)
}

/**
 * Closes what the tour opened in the navigation; a no-op otherwise. Waits a
 * moment first, so a page or band change has already reshaped the drawer
 * (a page change closes the overlay drawer by itself, a band change turns it
 * into the rail — then there is nothing left to close).
 */
async function releaseTourDrawer() {
	if (!tourDrawer.opened && !tourDrawer.expanded) return
	await pause(50)
	const drawerEl = navDrawer()
	if (tourDrawer.expanded) {
		tourDrawer.expanded = false
		drawerEl?.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }))
	}
	if (tourDrawer.opened) {
		tourDrawer.opened = false
		if (drawerEl && hasClass(drawerEl, 'temporary') && hasClass(drawerEl, 'active')) {
			document.querySelector<HTMLElement>(NAV_DRAWER_TOGGLE)?.click()
			await pause(300)
		}
	}
}

/** Logout: only forget, never touch the interface. */
function forgetTourDrawer() {
	tourDrawer.opened = false
	tourDrawer.expanded = false
}

/**
 * Steps that don't point into the navigation first close what the tour opened
 * there: the overlay drawer covers the page, the expanded rail the content
 * next to it.
 */
function withDrawerRelease(def: TourStepDefinition): TourStepDefinition {
	if (def.needsExpandedDrawer) return def
	const own = def.prepare
	return {
		...def,
		prepare: async (signal) => {
			await releaseTourDrawer()
			if (signal.aborted) return
			await own?.(signal)
		},
	}
}

// ---------------------------------------------------------------------------
// Schritte filtern und vorbereiten
// ---------------------------------------------------------------------------

/**
 * Resolves the steps for a band (other bands drop with reason `layout`), then
 * evaluates gates. Gate errors drop the step with reason `error` — the run
 * then counts as incomplete. Anchors are checked when the step is shown
 * (runStepPrepare), so a slowly loading page never drops a step.
 */
export async function filterSteps(
	registry: TourStepDefinition[],
	ctx: GateContext,
	{ band, log, tour }: { band: string; log: TourLogEntry[]; tour: string }
): Promise<TourStepDefinition[]> {
	const { steps, skipped } = resolveSteps(registry, band)
	log.push(...skipped)
	const checks = await Promise.all(
		steps.map(
			async (def): Promise<TourStepDefinition | { reason: TourSkipReason; detail?: string }> => {
				if (def.gate) {
					try {
						const outcome = gateOutcome(await def.gate(ctx))
						if (!outcome.show) return { reason: outcome.reason }
					} catch (err) {
						return { reason: 'error', detail: String(err) }
					}
				}
				return def
			}
		)
	)
	const out: TourStepDefinition[] = []
	steps.forEach((step, i) => {
		const check = checks[i]
		if (!check) return
		if ('reason' in check) logSkip(log, tour, step.id, check.reason, check.detail)
		else out.push(check)
	})
	return out
}

type StepCheck =
	| { ok: true }
	| { ok: false; aborted: true }
	| { ok: false; reason: TourSkipReason; detail?: string }

const ABORTED: StepCheck = { ok: false, aborted: true }

/**
 * Run a step's side-effects (route, drawer, prepare), then find, scroll to and
 * verify its anchor. Driver.js has no per-step beforeShowPromise — we call this
 * from the footer handlers BEFORE driver.moveTo(). Checks the abort signal
 * after every await: once the tour ended, nothing happens anymore.
 */
async function runStepPrepare(
	def: TourStepDefinition,
	signal: AbortSignal
): Promise<StepCheck> {
	try {
		if (def.route && router.currentRoute.value.name !== def.route) {
			await router.push({ name: def.route }).catch(() => undefined)
			if (signal.aborted) return ABORTED
		}
		if (def.needsExpandedDrawer) await ensureDrawerExpanded()
		if (signal.aborted) return ABORTED
		if (def.prepare) await def.prepare(signal)
		if (signal.aborted) return ABORTED
	} catch (err) {
		return { ok: false, reason: 'error', detail: String(err) }
	}
	if (def.verifyOnShow) {
		const verdict = def.verifyOnShow()
		if (verdict !== true) {
			return { ok: false, reason: verdict === false ? 'data' : verdict.skip }
		}
	}
	if (def.anchor) {
		const el = await findAnchor(def.anchor, 2500, signal)
		if (signal.aborted) return ABORTED
		if (!el) return { ok: false, reason: 'anchor', detail: def.anchor }
		await bringIntoView(el, signal)
		if (signal.aborted) return ABORTED
		// Liegt das Ziel unter einer festen Leiste (z. B. die App-Leiste, nachdem
		// der Vorschritt weiter unten stand), einmal mittig holen und neu prüfen.
		if (isCovered(el)) {
			await bringIntoView(el, signal, true)
			if (signal.aborted) return ABORTED
			if (isCovered(el)) return { ok: false, reason: 'covered', detail: def.anchor }
		}
	}
	return { ok: true }
}

// ---------------------------------------------------------------------------
// Ein Lauf
// ---------------------------------------------------------------------------
let currentDriver: Driver | null = null
let currentRun: { finish: (reason: TourEndReason) => void } | null = null
let starting = false
// Run-id guards an in-flight start() against being cancelled by a stop()
// while it's still awaiting navigation/gates. Each start() bumps the id and
// captures it locally; after every await it re-checks vs the module-level
// id and bails if a stop()/restart() has happened in the meantime.
let runId = 0

export type RunTourOptions = {
	/** Store key the outcome is remembered under (completed / skipped). */
	seenKey: number | string | undefined
	/** Probe of the caller's start attempt (stop() during gates/navigation). */
	isCancelled: () => boolean
	/**
	 * Closes what the tour itself opened, per end reason. On `logout` only
	 * release resources; on `external` close tour-owned surfaces if still
	 * there, never restore — the user's action wins.
	 */
	onEnd?: (reason: TourEndReason) => void
	/** Band the run started in; a change mid-run ends it with `band`. */
	band?: TourBandContext
	/** Run log, already holding the skips from filterSteps. */
	log?: TourLogEntry[]
	/** Name for the log (dev console, Cypress). */
	tour?: string
}

/**
 * Drives a filtered step list with driver.js. The returned promise settles
 * when the run has really ended (not when it started). Every end goes through
 * one idempotent `finish(reason)`: abort signal, listeners, driver, the
 * tour's own cleanup, "seen" state, log — exactly once, however many end
 * signals arrive.
 */
export async function runTour(
	steps: TourStepDefinition[],
	{
		seenKey,
		isCancelled,
		onEnd,
		band,
		log = [],
		tour = 'tour',
	}: RunTourOptions
): Promise<TourEndReason> {
	const tourStore = useTourStore()
	const controller = new AbortController()
	const { signal } = controller
	const stops: (() => void)[] = []
	let finished = false
	let driverRef: Driver | null = null
	// Zeigt das Fenster des aktuellen Schritts; ausgelöst von onHighlighted.
	let revealPopover: (() => Promise<void>) | null = null
	let resolveDone: (reason: TourEndReason) => void = () => {}
	const done = new Promise<TourEndReason>((resolve) => (resolveDone = resolve))

	const finish = (reason: TourEndReason) => {
		if (finished) return
		finished = true
		controller.abort()
		for (const stop of stops.splice(0)) {
			try {
				stop()
			} catch {
				/* ignore */
			}
		}
		// onDestroyed sees `finished` and does nothing.
		if (driverRef?.isActive()) {
			try {
				driverRef.destroy()
			} catch {
				/* ignore */
			}
		}
		try {
			onEnd?.(reason)
		} catch (err) {
			console.warn('[tour] cleanup failed:', err)
		}
		persistOutcome(reason, seenKey, log)
		publishRun(tour, reason, log, band?.current)
		tourStore.setActive(false)
		tourStore.setStepContext(null)
		if (currentDriver === driverRef) currentDriver = null
		if (currentRun?.finish === finish) currentRun = null
		resolveDone(reason)
	}
	currentRun = { finish }

	// Breite verlässt das Band → abbrechen, nie neu auflösen.
	if (band) {
		stops.push(
			watch(tourWindowWidth(), () => {
				if (band.now() !== band.current) finish('band')
			})
		)
	}
	// Browser-Zurück (und Android-Zurück, sofern es History nutzt) beendet die
	// Tour; die Navigation des Nutzers gilt.
	const onPopState = () => finish('external')
	window.addEventListener('popstate', onPopState)
	stops.push(() => window.removeEventListener('popstate', onPopState))

	/** First showable step from `from` in `direction`; logs every skip. */
	const seek = async (from: number, direction: 1 | -1): Promise<number | null> => {
		for (let i = from; i >= 0 && i < steps.length; i += direction) {
			const step = steps[i]
			if (!step) break
			const check = await runStepPrepare(step, signal)
			if ('aborted' in check) return null
			if (check.ok) return i
			if (direction === 1) logSkip(log, tour, step.id, check.reason, check.detail)
		}
		return null
	}

	// Build driver.js step list. `element` is a function so the anchor
	// selector is resolved at show-time (after runStepPrepare).
	const driveSteps: DriveStep[] = steps.map((def) => ({
		element: def.anchor
			? () =>
					// driver.js requires an Element; fall back to body so
					// the popover renders centered.
					firstVisible(def.anchor!) ?? document.body
			: undefined,
		popover: {
			title: def.title,
			description: def.text,
			side: def.placement ?? 'bottom',
			align: 'center',
			// We render our own footer via Teleport — hide driver's
			// next/previous buttons but keep the close X.
			showButtons: ['close'],
		},
	}))

	driverRef = driver({
		steps: driveSteps,
		animate: true,
		smoothScroll: true,
		allowClose: true,
		allowKeyboardControl: false, // TourFooter owns the keyboard handler
		stagePadding: 6,
		stageRadius: 8,
		overlayOpacity: 0.5,
		popoverClass: 'aaremo-tour-popover',
		disableActiveInteraction: true,
		// Clicking the dark overlay must NOT silently end the tour —
		// only an explicit click on ✕ / "Tour überspringen" / ESC does.
		overlayClickBehavior: () => {
			/* no-op */
		},
		onPopoverRender: (popover, opts) => {
			const idx = opts.driver.getActiveIndex() ?? 0
			const isFirst = idx === 0
			const isLast = idx === steps.length - 1
			const stepId = steps[idx]?.id ?? ''
			popover.wrapper.dataset.tourStep = stepId
			// Eigene Oberfläche: verdeckt für die Verdeckungsprüfung nichts.
			popover.wrapper.setAttribute(TOUR_UI_ATTR, '')
			// driver.js setzt beim Rendern `display: block` inline (und zwischen
			// Schritten `none`). Flex, damit bei geringer Höhe nur der Text scrollt
			// und Titel und Knöpfe sichtbar bleiben (App.vue).
			popover.wrapper.style.display = 'flex'
			log.push({ id: stepId, status: 'shown' })

			// Inject an empty slot DIV; TourFooter teleports the real
			// <ButtonIcon> components into it. Driver recycles the
			// wrapper between steps, so we look up before creating.
			let slot = popover.wrapper.querySelector<HTMLElement>(
				'.aaremo-tour-footer-slot'
			)
			if (!slot) {
				slot = document.createElement('div')
				slot.className = 'aaremo-tour-footer-slot'
				const desc = popover.description
				if (desc?.parentNode) {
					desc.parentNode.insertBefore(slot, desc.nextSibling)
				} else {
					popover.wrapper.appendChild(slot)
				}
			}

			// driver.js zeichnet das Fenster schon zur Hälfte der Kasten-Animation.
			// Bis zu deren Ende gilt intern noch das vorige Element als aktiv:
			// refresh() und Scroll-Ereignisse setzten das Fenster an den alten
			// Anker, es sprang dem Kasten danach hinterher. Deshalb unsichtbar
			// rendern und erst zeigen, wenn der Kasten angekommen ist und das Ziel
			// stillsteht. Dann einmal neu messen: erst jetzt ist auch der Footer
			// (Teleport) im Fenster, sonst wählt driver.js eine zu knappe Seite.
			const driverInstance = opts.driver
			popover.wrapper.classList.add(POPOVER_PENDING_CLASS)
			const reveal = async () => {
				if (revealPopover !== reveal) return
				revealPopover = null
				const anchor = driverInstance.getActiveElement()
				if (anchor) await untilSettled(anchor)
				if (signal.aborted || !driverInstance.isActive() || !popover.wrapper.isConnected) {
					return
				}
				if (anchor) resetClippedScroll(anchor)
				driverInstance.refresh()
				// refresh() positioniert im nächsten Frame, danach korrigieren und zeigen.
				requestAnimationFrame(() =>
					requestAnimationFrame(() => {
						keepInViewport(popover.wrapper)
						popover.wrapper.classList.remove(POPOVER_PENDING_CLASS)
					})
				)
			}
			revealPopover = reveal
			// Rückfall, falls onHighlighted ausbleibt: nie dauerhaft unsichtbar.
			setTimeout(reveal, 1500)

			tourStore.setStepContext({
				target: slot,
				isFirst,
				isLast,
				next: async () => {
					if (signal.aborted) return
					if (isLast) {
						finish('completed')
						return
					}
					const target = await seek(idx + 1, 1)
					if (signal.aborted || !driverInstance.isActive()) return
					if (target === null) {
						// Every remaining step was skipped — finish.
						finish('completed')
						return
					}
					driverInstance.moveTo(target)
				},
				back: async () => {
					if (signal.aborted) return
					if (isFirst) {
						finish('skipped')
						return
					}
					const target = await seek(idx - 1, -1)
					if (signal.aborted || !driverInstance.isActive()) return
					if (target !== null) driverInstance.moveTo(target)
				},
				cancel: () => finish('skipped'),
				complete: () => finish('completed'),
			})
		},
		// Kasten-Animation fertig (auch beim ersten Schritt, ca. 400 ms).
		onHighlighted: () => {
			void revealPopover?.()
		},
		onDeselected: () => {
			tourStore.setStepContext(null)
		},
		onCloseClick: () => finish('skipped'),
		// Ends through driver.js itself (e.g. destroy() from elsewhere).
		onDestroyed: () => finish('skipped'),
	})
	currentDriver = driverRef

	// Prepare the first showable step before drive() so its anchor exists.
	const first = await seek(0, 1)
	if (finished) return done
	if (isCancelled()) {
		finish('external')
		return done
	}
	if (first === null) {
		finish(isIncomplete(log) ? 'error' : 'empty')
		return done
	}
	try {
		tourStore.setActive(true)
		driverRef.drive(first)
	} catch (err) {
		console.warn('[tour] drive failed:', err)
		finish('error')
	}
	return done
}

/** True while any tour (portal, dashboard or cockpit) is starting or showing. */
export function isTourBusy(): boolean {
	return starting || !!currentRun || !!currentDriver?.isActive()
}

/**
 * Claims the single tour slot for one start attempt. Returns a cancel probe
 * for the caller's post-await checks, or null when a tour already runs.
 */
export function beginTourRun(): { isCancelled: () => boolean; end: () => void } | null {
	if (isTourBusy()) return null
	starting = true
	const myRunId = ++runId
	return {
		isCancelled: () => myRunId !== runId,
		end: () => {
			if (myRunId === runId) starting = false
		},
	}
}

export function useTour() {
	const userStore = useUserStore()
	const tourStore = useTourStore()
	const settingStore = useSettingStore()
	const { isAdmin, isTeam, whoami } = storeToRefs(userStore)
	const { person: liaPerson } = storeToRefs(useLiaStore())
	const { checkIfSubscribedTo } = useSubscription()
	const bandSpec = useTourBandSpec()

	/**
	 * Portal tour per band; `null` = no tour in this band. All bands share one
	 * registry, `bands`/`variants` on its steps adapt it (AAR-1994).
	 */
	const strands: Record<'narrow' | 'medium' | 'wide', (() => TourStepDefinition[]) | null> = {
		narrow: buildStepRegistry,
		medium: buildStepRegistry,
		wide: buildStepRegistry,
	}

	const currentBand = () => bandFor(tourWindowWidth().value, bandSpec())

	function canRun(): boolean {
		if (!whoami.value) return false
		if (isAdmin.value || isTeam.value) return false
		if (liaPerson.value) return false
		return strands[currentBand()] !== null
	}

	function shouldAutoStart(): boolean {
		if (autoStartSuppressedByCypress()) return false
		if (!canRun()) return false
		if (autoStartBlocked(whoami.value?.id)) return false
		return tourStore.shouldAutoStart(whoami.value?.id)
	}

	function gateContext(): GateContext {
		return { checkIfSubscribedTo, settings: settingStore.getData }
	}

	async function start({ force = false }: { force?: boolean } = {}) {
		if (!canRun()) return
		if (!force && !shouldAutoStart()) return

		const run = beginTourRun()
		if (!run) return
		const cancelled = run.isCancelled
		try {
			if (router.currentRoute.value.name !== 'dashboard') {
				await router.push({ name: 'dashboard' }).catch(() => undefined)
				if (cancelled()) return
				await findAnchor('dashboard', 3000)
				if (cancelled()) return
			}

			await waitForGone('.v-overlay--active', 1500)
			if (cancelled()) return

			const band = currentBand()
			const strand = strands[band]
			if (!strand) return
			const log: TourLogEntry[] = []
			const steps = await filterSteps(strand(), gateContext(), {
				band,
				log,
				tour: 'portal',
			})
			if (cancelled() || !canRun()) return
			if (steps.length === 0) return

			await runTour(steps.map(withDrawerRelease), {
				seenKey: whoami.value?.id,
				isCancelled: () => cancelled() || !canRun(),
				band: { current: band, now: currentBand },
				log,
				tour: 'portal',
				// Die von der Tour geöffnete Navigation wieder schließen; beim
				// Abmelden nur vergessen, nichts an der Oberfläche ändern.
				onEnd: (reason) => {
					if (reason === 'logout') forgetTourDrawer()
					else void releaseTourDrawer()
				},
			})
		} finally {
			run.end()
		}
	}

	async function restart() {
		// Only reset "seen" when there is a tour for this band — otherwise the
		// button would clear the status and start nothing.
		if (!canRun()) return
		stopTour()
		tourStore.reset(whoami.value?.id)
		await start({ force: true })
	}

	return {
		canRun,
		shouldAutoStart,
		gateContext,
		start,
		stop: stopTour,
		restart,
	}
}

/**
 * Under Cypress the tour auto-start races every spec that runs as a
 * non-team persona — the driver.js overlay covers the page mid-assert.
 * Auto-start is therefore OFF in E2E by default; tour specs opt back in
 * via the localStorage flag below. Explicit restarts (start({ force }))
 * are unaffected.
 */
export function autoStartSuppressedByCypress(): boolean {
	return (
		isCypress() &&
		window.localStorage.getItem('__cypress_tour_autostart__') !== '1'
	)
}

/**
 * End the running tour (if any) and invalidate a start in progress. Without a
 * reason this is an external end (navigation, unmount): nothing is remembered
 * as seen and no auto-start follows in this page load.
 */
export function stopTour({ reason = 'external' }: { reason?: TourEndReason } = {}) {
	runId++
	if (currentRun) currentRun.finish(reason)
	else if (currentDriver?.isActive()) currentDriver.destroy()
	currentDriver = null
	starting = false
	const tourStore = useTourStore()
	tourStore.setStepContext(null)
	tourStore.setActive(false)
}
